import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { List, PickerField, Store } from "@bryntum/schedulerpro";
import { debounce } from "../../../other/utils/otherUtils.jsx";

const request = {
  language: "en-US",
  includedRegionCodes: ["US"]
};

class SwivuAddressPicker extends PickerField {
  static get type() {
    return WIDGET_KEYS.swivuAddressPicker;
  }

  static get $name() {
    return "swivuAddressPicker";
  }

  static get configurable() {
    return {
      label: "up.label",
      name: "up.swivuPickerDisplayField",
      swivuRecordField: "up.swivuRecordField",
      placeholder: "up.placeholder",
      labelWidth: "up.labelWidth",
      cls: "!flex-1",
      listeners: {
        change: "onSwivuFieldChange",
        input: "onSwivuFieldInput",
        focusOut: "onFieldFocusOut"
      },
      triggers : {
        loader : {
          cls  : 'fa fa-spinner fa-spin relative right-2 !p-0',
          hidden: true
        },
      },
      // This is the picker configuration the combo widget uses internally, we are using it because we are expecting same behaviour as that field
      picker: {
        type: "list",
        floating: true,
        scrollAction: "realign",
        itemsFocusable: false,
        align: {
          align: "t0-b0",
          axisLock: true
        },
        maxHeight: 324,
        scrollable: {
          overflowY: true
        },
        autoShow: false,
        focusOnHover: false,
        listeners: {
          item: "up.onItemClick"
        }
      }
    };
  }

  init(record) {
    this.debouncedFetchAddress = debounce(this.fetchAddress.bind(this), 250);
    this.swivuRecord = record;
    const address = record[this.swivuRecordField];
    const { lat, lon } = address ?? {}; // Can be null for new records;
    if (lat && lon) {
      this.triggerResolved(address);
    } else {
      this.triggerUnresolved();
    }
  }

  onSwivuFieldChange(e) {
    const { userAction } = e;
    if (userAction) {
      this.triggerAddressChange(e);
      this.swivuRecord.reRender = !this.swivuRecord.reRender;
    }
  }

  async onItemClick(e) {
    const { record } = e;

    this.hidePicker();
    this.toggleLoader(true);

    this.input.value = record.formattedAddress; // Setting value programitcally into the input field as it takes time to fetch lat & lon before updating the record which then updates the input field value.

    const googlePlaceInstance = this.getGooglePlaceInstanceById(record.id);
    const place = googlePlaceInstance.placePrediction.toPlace();
    await place.fetchFields({ fields: ["location"] }); // The session concludes here

    const lat = place.location.lat();
    const lon = place.location.lng();
    record.lat = lat;
    record.lon = lon;

    this.toggleLoader(false);

    this.triggerResolved(record);
    this.triggerAddressChange(e);
  }

  onSwivuFieldInput({ value }) {
    this.triggerUnresolved();
    this.isFetchingAddress = true;
    this.debouncedFetchAddress();

    if (value === "") this.hidePicker();
  }

  onFieldFocusOut() {
    this.hidePicker();
    this.toggleLoader(false); // hide loader if it was shown in fetchAddress asynchronously
  }

  async fetchAddress() {
    if (this.containsFocus && window.googleMapsLoaded && this.input.value) {
      this.toggleLoader(true); // Show loader
      this.startSession(); // Start googles session, The function will only create new session if it doesn't exist
      this.showPicker();
      this.store.data = []; // clear data
      this.toggleEmptyText();

      request.input = this.input.value;
      request.sessionToken = this.googlePlacesSession;
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      this.googlePlaceSuggestions = suggestions; // Maintain a reference to the suggestions for later use

      this.store.data = suggestions.map((placeInstance) => {
        const placeName = placeInstance.placePrediction.text.toString();
        return {
          id: placeInstance.placePrediction.placeId,
          formattedAddress: placeName,
          lat: null,
          lon: null
        };
      });
      this.toggleEmptyText();
      this.toggleLoader(false); // Hide loader
    }

    this.isFetchingAddress = false;
  }

  startSession() {
    if (!this.googlePlacesSession) this.googlePlacesSession = new window.google.maps.places.AutocompleteSessionToken();
  }

  endSession() {
    this.googlePlacesSession = null;
  }

  triggerAddressChange(e) {
    this.trigger("swivuLocationChange", { ...e, source: this }); // source is changing if the listener is 'item' or 'change' for picker/list & input field respectively. We are unifying it here.
    this.endSession(); // Value has been peristed by container, end google's session
  }

  triggerUnresolved() {
    this.trigger("swivuLocationUnresolved");
  }

  triggerResolved(record) {
    this.trigger("swivuLocationResolved", record);
  }

  toggleLoader(isShow) {
    this.triggers.loader.hidden = !isShow;
  }

  toggleEmptyText() {
    this.picker.element.classList.toggle("swivu-hide-empty-text");
  }


  /* This function is being used by PickerField internally, The widget's picker is not contructed unless we create an implmenation of this function in subclass, As per our observation the function is only invoked once for the field, We are using similar configuration as the combo widget since we expect same results.
  We haven't changed much of the implmentation of this function from the combo. */
  changePicker(picker, oldPicker) {
    this.store = new Store(); // Create store for the picker
    if (picker) {
      const config = List.mergeConfigs({
          owner: this,
          store: this.store,
          displayField: "formattedAddress", // we are harcoding it as the google records in the store will have a 'formattedAddress' field as string value
          forElement: this.pickerAlignElement,
          align: {
            match: 'min', // Makes the combo box width match the maximum amount of text
            target: this.pickerAlignElement,
            minHeight: Math.min(3, this.store.count) * 40
          },
          navigator: {
            keyEventTarget: this.input,
            activateOnMouseover: true
          }
        }, picker);

      picker = List.reconfigure(oldPicker, picker ? config : null, this);
      picker.element.classList.add("b-combo-picker"); // This class is necessary for showing/hiding the empty text
      picker.element.dataset.emptyText = "No matching locations found"; // This text is shown using css ::before pseudo-element
    } else {
      oldPicker?.destroy();
    }

    return picker;
  }

  getGooglePlaceInstanceById(id) {
    return this.googlePlaceSuggestions.find((googlePlaceInstance) => googlePlaceInstance.placePrediction.placeId === id);
  }
}

SwivuAddressPicker.initClass();
